import { z } from 'zod';
import { ASPECT_RATIO, STORYBOARD_STATUS, SCENE_STATUS } from '../types/common.js';

export const storyboardSchema = z.object({
  id: z.string().min(1),
  productId: z.string().min(1),
  conceptId: z.string().min(1).nullable().optional(),
  promptPackageId: z.string().min(1).nullable().optional(),
  hook: z.string().min(1),
  audience: z.string().min(1),
  objective: z.string().min(1),
  tone: z.string().min(1),
  cta: z.string().min(1),
  durationSeconds: z.number().int().positive().max(180),
  aspectRatio: z.enum(ASPECT_RATIO),
  language: z.string().min(2).default('id'),
  status: z.enum(STORYBOARD_STATUS),
  createdAt: z.string(),
  updatedAt: z.string().nullable().optional(),
});
export type Storyboard = z.infer<typeof storyboardSchema>;

export const createStoryboardInputSchema = z.object({
  productId: z.string().min(1),
  conceptId: z.string().min(1).optional(),
  promptPackageId: z.string().min(1).optional(),
  hook: z.string().min(1).max(500),
  audience: z.string().min(1).max(500),
  objective: z.string().min(1).max(500),
  tone: z.string().min(1).max(200),
  cta: z.string().min(1).max(200),
  durationSeconds: z.number().int().positive().max(180).default(30),
  aspectRatio: z.enum(ASPECT_RATIO).default('9:16'),
  language: z.string().min(2).default('id'),
});
export type CreateStoryboardInput = z.infer<typeof createStoryboardInputSchema>;

export const updateStoryboardInputSchema = createStoryboardInputSchema
  .omit({ productId: true })
  .partial();
export type UpdateStoryboardInput = z.infer<typeof updateStoryboardInputSchema>;

/**
 * SocialCaptions — output PROMPT_EXPORT stage, level storyboard (bukan per-scene).
 * Disimpan di `generation_jobs.mock_result.socialCaptions` (JSON blob).
 *
 * Caption sosmed siap copy-paste untuk memposting video iklan yang sudah dibuat.
 * Bahasa caption mengikuti bahasa storyboard (default: Indonesia).
 */
export const socialCaptionsSchema = z.object({
  instagram: z
    .string()
    .min(1)
    .describe(
      'Caption Instagram — storytelling engaging dengan emoji yang relevan, CTA jelas di akhir, ' +
      'dan 10-15 hashtag campuran populer + niche. Min 3 paragraf pendek. Bahasa: sesuai bahasa storyboard.',
    ),
  tiktok: z
    .string()
    .min(1)
    .describe(
      'Caption TikTok — dimulai dengan HOOK yang langsung menarik perhatian (1-2 kalimat pertama). ' +
      'Pendek dan punchy, max 5-7 baris. Akhiri dengan CTA + 5-8 hashtag trending dan relevan.',
    ),
  twitter: z
    .string()
    .min(1)
    .describe(
      'Caption Twitter/X — singkat, impactful, max 240 karakter untuk tweet utama. ' +
      'Boleh format thread (tandai dengan 1/ 2/ dst) kalau perlu lebih panjang. Tanpa hashtag berlebihan.',
    ),
  facebook: z
    .string()
    .min(1)
    .describe(
      'Caption Facebook — lebih panjang dan informatif dibanding TikTok. ' +
      'Ceritakan manfaat produk, sertakan CTA, dan 3-5 hashtag relevan.',
    ),
});
export type SocialCaptions = z.infer<typeof socialCaptionsSchema>;

/**
 * FormattedPrompts — output PROMPT_EXPORT stage.
 * Disimpan sebagai JSON di kolom `scenes.formatted_prompts`.
 *
 * Setiap field adalah prompt siap-copy untuk tool yang bersangkutan.
 * Deskripsi di setiap field dikirim ke LLM via JSON Schema (response_format)
 * sehingga model memahami standar kualitas yang diharapkan per tool.
 *
 * Tools: midjourney, sora, kling, runway, veo, generic
 */
export const formattedPromptsSchema = z.object({
  midjourney: z
    .string()
    .min(1)
    .describe(
      'Prompt untuk Midjourney — deskripsi visual SANGAT DETAIL dalam bahasa Inggris (min 30 kata). ' +
      'Inklusif: subjek + aksi, setting, pencahayaan spesifik, sudut kamera, mood, style artistik. ' +
      'Diakhiri dengan parameter teknis: --ar X:Y --v 6 --style raw',
    ),
  sora: z
    .string()
    .min(1)
    .describe(
      'Prompt untuk Sora — kalimat deskriptif SINEMATIK dalam bahasa Inggris (min 2-3 kalimat penuh). ' +
      'Fokus pada GERAKAN, perubahan yang terjadi, dan suasana emosional. ' +
      'Mulai dengan tipe shot + gerakan kamera (contoh: "A slow dolly-in shot...")',
    ),
  kling: z
    .string()
    .min(1)
    .describe(
      'Prompt untuk Kling — deskripsi aksi REALISTIS dalam bahasa Inggris (min 2 kalimat). ' +
      'Format: [subjek + aksi], [durasi]s, [kualitas teknis], [pencahayaan], [detail spesifik]',
    ),
  runway: z
    .string()
    .min(1)
    .describe(
      'Prompt untuk Runway Gen-3 Alpha — 2-3 kalimat dalam bahasa Inggris. ' +
      'Fokus pada motion descriptor, camera movement, dan visual atmosphere. ' +
      'Singkat tapi kaya motion direction.',
    ),
  veo: z
    .string()
    .min(1)
    .describe(
      'Prompt untuk Google Veo 2 — 3-4 kalimat natural-language SINEMATIK dalam bahasa Inggris. ' +
      'TIDAK menggunakan parameter syntax khusus — murni kalimat deskriptif. ' +
      'Struktur optimal: (1) Temporal opening "Opens with..." atau "A [shot type] of...", ' +
      '(2) Aksi/gerakan yang terjadi secara sekuensial, ' +
      '(3) Kualitas sinematik: pencahayaan, color grade, lens, atmosphere, ' +
      '(4) Akhiri dengan format hint: "portrait format" (9:16) atau "cinematic widescreen" (16:9). ' +
      'Gunakan kata kerja temporal (opens, transitions, reveals, closes) untuk mendeskripsikan alur klip. ' +
      'Contoh: "Opens with a close-up of elegant hands slowly lifting a frosted glass serum bottle from a marble vanity, ' +
      'warm morning sunlight streaming through sheer curtains and creating soft golden lens flare. ' +
      'The camera gently pulls back, revealing a woman in her 30s with a serene, confident expression as she applies the product, ' +
      'her reflection visible in a minimalist bathroom mirror. ' +
      'Cinematic color grade with warm tones, shallow depth of field, 4K photorealistic quality, portrait format."',
    ),
  generic: z
    .string()
    .min(1)
    .describe(
      'Prompt generik bersih dalam bahasa Inggris (min 2 kalimat). ' +
      'Tanpa parameter tool-specific. Cocok untuk Stable Diffusion, DALL-E, Leonardo, Ideogram. ' +
      'Detail visual penuh tanpa syntax khusus tool manapun.',
    ),
});
export type FormattedPrompts = z.infer<typeof formattedPromptsSchema>;

/**
 * Scene — PRD §14.
 */
export const sceneSchema = z.object({
  id: z.string().min(1),
  storyboardId: z.string().min(1),
  orderIndex: z.number().int().nonnegative(),
  durationSeconds: z.number().positive(),
  visualDescription: z.string().min(1),
  scenePrompt: z.string().min(1).nullable().optional(),
  voiceoverLine: z.string().min(1).nullable().optional(),
  captionText: z.string().min(1).nullable().optional(),
  formattedPrompts: formattedPromptsSchema.nullable().optional(),
  assetIds: z.array(z.string()).default([]),
  status: z.enum(SCENE_STATUS),
  createdAt: z.string(),
  updatedAt: z.string().nullable().optional(),
});
export type Scene = z.infer<typeof sceneSchema>;

/**
 * Output LLM generate_storyboard — array scene mentah dari STORYBOARD_DRAFT.
 *
 * Setiap field memiliki `.describe()` yang dikirim ke model via JSON Schema
 * (response_format.json_schema) sehingga model mendapat guidance di dua level:
 * (1) system prompt (narasi panjang) dan (2) schema field description (konkret, per-field).
 */
export const generateStoryboardOutputSchema = z.object({
  scenes: z
    .array(
      z.object({
        durationSeconds: z
          .number()
          .positive()
          .max(30)
          .describe('Durasi klip dalam detik. Integer, min 3, max 15. Total semua scene harus mendekati durasi video.'),
        visualDescription: z
          .string()
          .min(1)
          .describe(
            'Deskripsi visual SINEMATIK seperti arahan sutradara — min 4 kalimat. ' +
            'Wajib mencakup: (1) siapa + aksi spesifik, (2) lokasi + detail setting, ' +
            '(3) jenis pencahayaan (golden hour/studio/natural window/neon), ' +
            '(4) tipe shot (close-up/wide/overhead/POV) + gerakan kamera (static/dolly/handheld). ' +
            'JANGAN tulis kalimat generik "scene ini menampilkan...".',
          ),
        scenePrompt: z
          .string()
          .min(1)
          .describe(
            'Prompt teknis DETAIL untuk AI image/video generation — min 3 kalimat. ' +
            'Inklusif: subjek + aksi, setting + pencahayaan dengan kata sifat konkret, ' +
            'tipe lensa/kamera, depth of field, style visual (cinematic/editorial/commercial). ' +
            'Ini basis untuk Midjourney, Sora, Kling.',
          ),
        voiceoverLine: z
          .string()
          .min(1)
          .describe(
            'SKRIP NARASI LENGKAP yang diucapkan narrator selama scene — WAJIB min 2-3 kalimat penuh. ' +
            'INI ADALAH KATA-KATA YANG BENAR-BENAR DIUCAPKAN — bukan deskripsi scene, bukan label. ' +
            'Harus natural seperti skrip iklan TV/digital profesional. ' +
            'JANGAN kosong, JANGAN 1 kata, JANGAN tulis "(narasi)" atau "(musik)". ' +
            'Contoh BAIK: "Pernahkah kamu merasa kulitmu tidak pernah cukup cerah, ' +
            'meski sudah mencoba berbagai produk? Kami mengerti. Dan kami punya jawabannya." ' +
            'Contoh BURUK: "Narasi pembuka" atau "Voiceover tentang produk"',
          ),
        captionText: z
          .string()
          .min(1)
          .describe(
            'Teks ON-SCREEN yang TERLIHAT di video (bukan narasi VO). ' +
            'Bisa berupa: tagline impactful, kata kunci, angka/statistik, highlight pesan utama. ' +
            'Singkat: 1-2 kalimat pendek atau 3-7 kata per baris.',
          ),
      }),
    )
    .min(1)
    .max(20),
});
export type GenerateStoryboardOutput = z.infer<typeof generateStoryboardOutputSchema>;

/**
 * Output LLM generate_formatted_prompts — hasil PROMPT_EXPORT.
 * Satu LLM call untuk seluruh storyboard:
 * - `scenes`        : formatted prompts per scene (Midjourney, Sora, Kling, Runway, Generic)
 * - `socialCaptions`: caption sosmed siap copy untuk keseluruhan video (Instagram, TikTok, Twitter, Facebook)
 */
export const generateFormattedPromptsOutputSchema = z.object({
  scenes: z
    .array(formattedPromptsSchema)
    .min(1)
    .max(20)
    .describe('Array formatted prompts per scene — urutannya HARUS sama dengan urutan scene input.'),
  socialCaptions: socialCaptionsSchema,
});
export type GenerateFormattedPromptsOutput = z.infer<typeof generateFormattedPromptsOutputSchema>;

/**
 * Input `POST /api/bff/scenes/:id/regenerate`
 */
export const regenerateSceneInputSchema = z.object({
  scenePrompt: z.string().min(1).optional(),
  visualDescription: z.string().min(1).optional(),
});
export type RegenerateSceneInput = z.infer<typeof regenerateSceneInputSchema>;
